import { useState, useMemo, useCallback, memo } from 'react';
import { Edit, Trash2, Search, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataTable } from '@/components/tables/data-table';
import { createAdvertiserColumns } from '@/components/tables/advertiser-columns';

interface Advertiser {
  id: number;
  name: string;
  address: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  campaignCount?: number;
}

interface AdvertiserListProps {
  advertisers: Advertiser[];
  isLoading?: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onEdit: (advertiser: Advertiser) => void;
  onDelete: (id: number) => Promise<void>;
}

export function AdvertiserList({
  advertisers,
  isLoading = false,
  searchTerm,
  onSearchChange,
  onEdit,
  onDelete,
}: AdvertiserListProps) {
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');

  // STEP 2: Memoize date formatter with useCallback
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  }, []);

  // STEP 5: Stabilize delete handler for row component
  const handleDeleteClick = useCallback((id: number) => {
    setDeleteId(id);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    setDeleteError('');

    try {
      await onDelete(deleteId);
      setDeleteId(null);
    } catch (error: any) {
      setDeleteError(error?.message || 'Failed to delete advertiser');
    } finally {
      setIsDeleting(false);
    }
  };

  // Create columns
  const columns = useMemo(
    () =>
      createAdvertiserColumns({
        onEdit,
        onDelete: handleDeleteClick,
        formatDate,
      }),
    [onEdit, handleDeleteClick, formatDate]
  );

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading advertisers...</div>
      ) : advertisers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No advertisers yet.</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <DataTable
            columns={columns}
            data={advertisers}
            searchKey="name"
            searchPlaceholder="Search by name or contact..."
            showColumnToggle={true}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Advertiser</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this advertiser? This action will soft-delete the
              record and archive it.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-700">{deleteError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
